export interface Stream {
  userId: string;
  userLogin: string;
  userName: string;
  profileImageUrl: string;
  title: string;
  startedAt: string;
  viewerCount: number;
  thumbnailUrl: string;
}

export class TwitchStreamsService {
  constructor(
    private clientId: string,
    private accessToken: string
  ) {}

  private async fetchWithAuth(url: string, method: string = 'GET') {
    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Client-Id': this.clientId
        }
      });

      // For POST requests (like raids), just return the response
      if (method === 'POST') {
        return response;
      }

      if (!response.ok) {
        const status = response.status;
        
        // More specific handling for auth errors
        if (status === 401) {
          throw new Error('auth_required: Authentication token expired or invalid');
        }
        
        if (status === 403) {
          throw new Error('auth_required: Insufficient permissions for this request');
        }
        
        throw new Error(`HTTP error! status: ${status}`);
      }

      // For GET requests, parse the JSON
      return response.json();
    } catch (error) {
      // Re-throw auth_required errors
      if (error instanceof Error && error.message.includes('auth_required')) {
        console.error('Auth error in TwitchStreamsService:', error.message);
        throw error;
      }
      // Re-throw other errors with more context
      throw new Error(`Failed to fetch from Twitch API: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  public async getFollowedStreams(): Promise<Stream[]> {
    const userJson = localStorage.getItem('twitch_user');
    if (!userJson) {
      throw new Error('User not found');
    }

    const user = JSON.parse(userJson) as { id: string };
    if (!user.id) {
      throw new Error('User ID not found');
    }

    // First get followed streams
    const streamsResponse = await this.fetchWithAuth(
      `https://api.twitch.tv/helix/streams/followed?user_id=${user.id}`
    );

    if (!streamsResponse.data?.length) {
      return [];
    }

    // Get user data for profile images
    const userIds = streamsResponse.data.map((stream: any) => stream.user_id);
    const usersResponse = await this.fetchWithAuth(
      `https://api.twitch.tv/helix/users?id=${userIds.join('&id=')}`
    );

    interface TwitchUserData {
      id: string;
      profile_image_url: string;
    }

    interface TwitchStreamData {
      user_id: string;
      user_login: string;
      user_name: string;
      title: string;
      started_at: string;
      viewer_count: number;
      thumbnail_url: string;
    }

    // Create a map of user data
    const userMap = new Map<string, TwitchUserData>(
      usersResponse.data.map((user: TwitchUserData) => [user.id, user])
    );

    // Combine stream and user data
    return streamsResponse.data.map((stream: TwitchStreamData) => {
      const userData = userMap.get(stream.user_id);
      // Replace {width} and {height} with actual dimensions
      const thumbnailUrl = stream.thumbnail_url
        .replace('{width}', '640')
        .replace('{height}', '360');
      
      return {
        userId: stream.user_id,
        userLogin: stream.user_login,
        userName: stream.user_name,
        profileImageUrl: userData?.profile_image_url || '',
        title: stream.title,
        startedAt: stream.started_at,
        viewerCount: stream.viewer_count,
        thumbnailUrl
      };
    });
  }

  public async getStreamByUserId(userId: string): Promise<Stream | null> {
    try {
      const response = await this.fetchWithAuth(
        `https://api.twitch.tv/helix/streams?user_id=${userId}`
      );

      if (!response.data?.[0]) {
        return null;
      }

      const stream = response.data[0];
      const userData = await this.getUserById(stream.user_id);
      
      return {
        userId: stream.user_id,
        userLogin: stream.user_login,
        userName: stream.user_name,
        profileImageUrl: userData?.profile_image_url || '',
        title: stream.title,
        startedAt: stream.started_at,
        viewerCount: stream.viewer_count,
        thumbnailUrl: stream.thumbnail_url
          .replace('{width}', '640')
          .replace('{height}', '360')
      };
    } catch (error) {
      console.error('Error fetching stream data:', error);
      return null;
    }
  }

  public async getStreamsByUserIds(userIds: string[]): Promise<Stream[]> {
    if (!userIds.length) {
      return [];
    }

    try {
      // Batch the user IDs to avoid URL length limits (Twitch supports up to 100 user_id params)
      const batchSize = 100;
      const batches = [];
      
      for (let i = 0; i < userIds.length; i += batchSize) {
        batches.push(userIds.slice(i, i + batchSize));
      }

      const allStreams: Stream[] = [];

      for (const batch of batches) {
        const streamParams = batch.map((id: string) => `user_id=${id}`).join('&');
        const streamsResponse = await this.fetchWithAuth(
          `https://api.twitch.tv/helix/streams?${streamParams}`
        );

        if (!streamsResponse.data?.length) {
          continue;
        }

        // Get user data for profile images for this batch
        const streamUserIds = streamsResponse.data.map((stream: any) => stream.user_id);
        const userParams = streamUserIds.map((id: string) => `id=${id}`).join('&');
        const usersResponse = await this.fetchWithAuth(
          `https://api.twitch.tv/helix/users?${userParams}`
        );

        interface TwitchUserData {
          id: string;
          profile_image_url: string;
        }

        interface TwitchStreamData {
          user_id: string;
          user_login: string;
          user_name: string;
          title: string;
          started_at: string;
          viewer_count: number;
          thumbnail_url: string;
        }

        // Create a map of user data for this batch
        const userMap = new Map<string, TwitchUserData>();
        if (usersResponse.data) {
          usersResponse.data.forEach((user: TwitchUserData) => {
            userMap.set(user.id, user);
          });
        }

        // Convert stream data for this batch
        const batchStreams = streamsResponse.data.map((stream: TwitchStreamData) => {
          const userData = userMap.get(stream.user_id);
          const thumbnailUrl = stream.thumbnail_url
            .replace('{width}', '640')
            .replace('{height}', '360');
          
          return {
            userId: stream.user_id,
            userLogin: stream.user_login,
            userName: stream.user_name,
            profileImageUrl: userData?.profile_image_url || '',
            title: stream.title,
            startedAt: stream.started_at,
            viewerCount: stream.viewer_count,
            thumbnailUrl
          };
        });

        allStreams.push(...batchStreams);
      }

      return allStreams;
    } catch (error) {
      console.error('Error fetching batch stream data:', error);
      return [];
    }
  }

  public async getUserById(userId: string): Promise<{
    id: string;
    login: string;
    display_name: string;
    profile_image_url: string;
  } | null> {
    try {
      const response = await this.fetchWithAuth(
        `https://api.twitch.tv/helix/users?id=${userId}`
      );

      if (!response.data?.[0]) {
        return null;
      }

      return response.data[0];
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }

  public async raidChannel(targetChannelId: string): Promise<void> {
    const userJson = localStorage.getItem('twitch_user');
    if (!userJson) {
      throw new Error('User not found');
    }

    const user = JSON.parse(userJson) as { id: string };
    if (!user.id) {
      throw new Error('User ID not found');
    }

    try {
      const response = await this.fetchWithAuth(
        `https://api.twitch.tv/helix/raids?from_broadcaster_id=${user.id}&to_broadcaster_id=${targetChannelId}`,
        'POST'
      );

      if (!response.ok) {
        const errorData = await response.json();
        // Twitch returns error details in the message field
        if (errorData && errorData.message) {
          throw new Error(errorData.message);
        } else {
          throw new Error(`Failed to start raid (${response.status})`);
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to start raid');
    }
  }
}
